import {
    Text,
} from '@mantine/core';
import { useState } from 'react';
import {
    CardElement,
    AddressElement,
    useElements,
    useStripe,
} from '@stripe/react-stripe-js';
import { useAddPaymentMethodMutation } from 'api/subscriptionPlanEndpoints';
import useGetUser from 'hooks/useGetUser';
import { Checkbox, Heading, Modal, Section } from '@carbon/react';
import { defineMessages, useIntl } from 'react-intl';
import sharedMessages from 'Components/utils/sharedMessages';
import styles from './AddPaymentMethodModal.module.scss';

interface Props {
  opened: boolean;
  onClose: () => void;
}

export const AddPaymentMethodModal = ({ opened, onClose }: Props) => {
    const stripe = useStripe();
    const elements = useElements();
    const intl = useIntl();

    const [setAsDefault, setSetAsDefault] = useState(true);

    const [addPaymentMethod, { isLoading }] = useAddPaymentMethodMutation();

    const user = useGetUser();

    if (!user) {
        return null;
    }

    const handleSubmit = async () => {
        if (!stripe || !elements) return;

        const cardElement = elements.getElement(CardElement);
        const addressElement = elements.getElement(AddressElement);

        if (!cardElement || !addressElement) {
            return;
        }

        const addressResult = await addressElement.getValue();
        if (!addressResult.complete) {
            return;
        }

        const result = await stripe.createPaymentMethod({
            type: 'card',
            card: cardElement,
            billing_details: {
                name: addressResult.value.name,
                email: user.email,
                address: {
                    line1: addressResult.value.address.line1,
                    line2: addressResult.value.address.line2,
                    city: addressResult.value.address.city,
                    state: addressResult.value.address.state,
                    postal_code: addressResult.value.address.postal_code,
                    country: addressResult.value.address.country,
                },
            },
        });

        if (result.error) {
            console.error(result.error);
            return;
        }

        await addPaymentMethod({
            setAsDefault,
            paymentDetails: {
                paymentToken: result.paymentMethod.id,
                paymentMethodType: result.paymentMethod.type,
                lastFourDigits: result.paymentMethod.card?.last4 ?? 'Hidden',
                cardBrand: result.paymentMethod.card?.brand ?? 'Hidden',
                expirationMonth: result.paymentMethod.card?.exp_month ?? -1,
                expirationYear: result.paymentMethod.card?.exp_year.toString() ?? '-1',
                billingAddress: {
                    fullName: addressResult.value.name,
                    email: user.email,
                    line1: addressResult.value.address.line1,
                    line2: addressResult.value.address.line2,
                    city: addressResult.value.address.city,
                    state: addressResult.value.address.state,
                    postalCode: addressResult.value.address.postal_code,
                    country: addressResult.value.address.country,
                },
                isDefault: setAsDefault,
            },
        }).unwrap();

        onClose();
    };

    return (
        <Modal
            preventCloseOnClickOutside
            open={opened}
            onRequestClose={onClose}
            primaryButtonDisabled={isLoading}
            title={intl.formatMessage(messages.addCard)}
            onRequestSubmit={handleSubmit}
            modalHeading={intl.formatMessage(messages.addCard)}
            loadingStatus={isLoading ? 'active' : undefined}
            modalLabel={intl.formatMessage(messages.areYouSure)}
            primaryButtonText={intl.formatMessage(sharedMessages.submit)}
            closeButtonLabel={intl.formatMessage(sharedMessages.cancel)}
            secondaryButtonText={intl.formatMessage(sharedMessages.back)}
        >
            <Section>
                <Text size="sm" c="dimmed">
                    {intl.formatMessage(messages.explanation)}
                </Text>

                <Section>
                    <Heading className={styles.heading}>
                        {intl.formatMessage(sharedMessages.card)}
                    </Heading>
                    <CardElement
                        options={{
                            hidePostalCode: true,
                        }}
                    />
                </Section>

                <Section>
                    <Heading className={styles.heading}>
                        {intl.formatMessage(sharedMessages.billingAddress)}
                    </Heading>
                    <AddressElement
                        options={{
                            mode: 'billing',
                            allowedCountries: ['PL'],
                        }}
                    />
                </Section>
                <div className={styles.checkbox}>
                    <Checkbox
                        id="new-default-payment-method"
                        labelText={intl.formatMessage(messages.defaultPaymentMethod)}
                        checked={setAsDefault}
                        onChange={(e) => { setSetAsDefault(e.currentTarget.checked); }}
                    />
                </div>
            </Section>
        </Modal>
    );
};

const messages = defineMessages({
    addCard: {
        id: 'src.Screens.Dashboard.Subscription.PaymentMethods.AddPaymentMethodModal.AddPaymentMethodModal.changingCard',
        defaultMessage: 'Dodaj metodę płatności',
    },
    areYouSure: {
        id: 'src.Screens.Dashboard.Subscription.PaymentMethods.AddPaymentMethodModal.AddPaymentMethodModal.areYouSure',
        defaultMessage: 'Uzupełnij dane karty, żeby dokonać nią płatności za subskrypcję',
    },
    explanation: {
        id: 'src.Screens.Dashboard.Subscription.PaymentMethods.AddPaymentMethodModal.AddPaymentMethodModal.explanation',
        defaultMessage: 'Dane karty są przetwarzane bezpiecznie przez zabezpieczony protokół.',
    },
    defaultPaymentMethod: {
        id: 'src.Screens.Dashboard.Subscription.PaymentMethods.AddPaymentMethodModal.AddPaymentMethodModal.defaultPaymentMethod',
        defaultMessage: 'Ustaw jako domyślną metodę płatności',
    },
});
